/**
 * 업무유형(workitem_type) 조회 훅 (TanStack Query)
 *
 * - GET /workitem-types?moduleCode=&workspace=   업무유형 목록(워크스페이스 활성)
 *
 * 동적 메타모델: 업무유형은 시드/회사 정의 데이터. 모듈별 하드코딩 분기 금지.
 * 사이드바·라우트는 이 목록(typeCode·moduleCode·nameI18n)으로 런타임 구성함.
 * 표시 라벨은 nameI18n + resolveLabel 로 해석.
 */
import { useQuery } from "@tanstack/react-query";
import type { I18nLabel, ModuleCode, WorkitemType } from "@eqms/shared-types";

import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

/** GET /workitem-types 응답 항목 (다국어 라벨 포함) */
export interface WorkitemTypeResponse
  extends Pick<WorkitemType, "id" | "typeCode" | "name" | "moduleCode" | "icon" | "singletonYn"> {
  /** 다국어 라벨 맵 (`{ko,en,zh,ja}`) */
  nameI18n?: I18nLabel;
  /** 부제(요약) 다국어 맵 — 페이지 헤더 .desc (HTML 시안). 서버 제공 시. */
  summaryI18n?: I18nLabel;
  /** 생성 버튼 라벨 다국어 맵 (예: CHG="변경 요청"). 없으면 "신규 {유형}" 폴백. */
  createLabelI18n?: I18nLabel;
  /** 목록 검색 placeholder 다국어 맵 (V111, 예: NC="NCR 번호·제목 검색"). 없으면 접두어 조립 폴백. */
  searchPhI18n?: I18nLabel;
  /** 정렬 순서 (서버 제공 시) */
  sort?: number;
  /** 등록 전자서명 필수(V123) — 결재선 관리가 작성자 고정 '등록' 행으로 표시. */
  createEsignRequired?: boolean;
}

interface UseWorkitemTypesOptions {
  /** 특정 모듈로 필터 */
  moduleCode?: ModuleCode;
}

/**
 * 워크스페이스 활성 업무유형 목록.
 * @param workspaceSlug 현재 워크스페이스 slug (X-Workspace-Slug 헤더 + 쿼리)
 */
export function useWorkitemTypes(
  workspaceSlug: string | undefined,
  options: UseWorkitemTypesOptions = {},
) {
  const { moduleCode } = options;

  return useQuery({
    queryKey: queryKeys.workitemTypes.list(workspaceSlug ?? "", { moduleCode }),
    queryFn: () => {
      const params = new URLSearchParams();
      if (workspaceSlug) params.set("workspace", workspaceSlug);
      if (moduleCode) params.set("moduleCode", moduleCode);
      const qs = params.toString();
      return apiFetch<WorkitemTypeResponse[]>(`/api/workitem-types${qs ? `?${qs}` : ""}`, {
        workspaceSlug,
      });
    },
    enabled: Boolean(workspaceSlug),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
